/* eslint-disable max-lines */
import ReactPlayer from 'react-player/lazy';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { playbackRates } from '@utils/constants/playback-rates.ts';
import { useAudio } from '@utils/providers/audio-context.tsx';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { formatTime } from '@utils/audio.ts';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { css, StyleSheet } from 'aphrodite';
import { useSharedCaption } from '@utils/providers/shared-caption-context.tsx';
import audioThumbnail from '@assets/images/audio-thumbnail.png';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { useTranslation } from 'react-i18next';
import {
  Clock,
  Link2,
  Mic,
  Palette,
  Pause,
  Play,
  Redo2,
  Unlink2,
  Undo2
} from 'lucide-react';
import SharedCaptionMediaSlider from '@components/editors/shared-editor/shared-caption/shared-caption-media-slider.tsx';
import { useThemeVars } from '@utils/hooks/use-theme-vars.ts';
import SharedTimingShiftControl from '@components/editors/shared-editor/shared-caption/shared-time-shift-control.tsx';
import { shortcutLabel } from '@utils/editors/shortcut-label.tsx';
import { SharedCaptionStyleModal } from '@components/editors/shared-editor/shared-caption/shared-caption-style-modal.tsx';

export const SharedCaptionMediaPlayer = () => {
  const { t } = useTranslation();
  const {
    session,
    handleMediaElementReady,
    document: myDocument
  } = useSharedCaption();
  const {
    play,
    pause,
    onMediaPause,
    onProgress,
    setRef,
    currentTime,
    isPlaying,
    rewind,
    forward,
    isMuted,
    playbackRate,
    setPlaybackRate,
    setDuration,
    linkedPlayback,
    toggleLinkedPlayback
  } = useAudio();

  const ref = useRef<ReactPlayer>(null);
  const divRef = React.useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { getColour } = useThemeContext();
  const { vars, cls } = useThemeVars();
  const [isStyleModalOpen, setIsStyleModalOpen] = useState(false);
  const [isTimingOffsetOpen, setIsTimingOffsetOpen] = useState(false);

  useEffect(() => {
    if (ref.current) {
      setRef(ref);
    }
  }, [setRef]);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        mainContainer: {
          backgroundColor: getColour('secondLayer')
        },
        mainText: {
          color: getColour('normalText')
        },
        iconsColor: {
          color: getColour('icon')
        }
      }),
    [getColour]
  );

  const hasMedia = !!session?.media_url;
  const isAudio = useMemo(
    () => session?.media_type === 'audio' || !session?.media_url,
    [session]
  );

  // Track which caption segment is currently visible. Only update when the
  // active segment actually changes — not on every 300ms progress tick.
  const lastFilteredRef = useRef(
    myDocument?.document
      ?.filter(
        i => i.start <= currentTime * 1000 && i.stop >= currentTime * 1000
      )
      ?.slice(-1)
  );
  const [filteredItems, setFilteredItems] = useState(lastFilteredRef.current);

  useEffect(() => {
    const timeMs = currentTime * 1000;
    const next = myDocument?.document
      ?.filter(i => i.start <= timeMs && i.stop >= timeMs)
      ?.slice(-1);

    const prevItem = lastFilteredRef.current?.[0];
    const nextItem = next?.[0];

    if (
      prevItem?.start !== nextItem?.start ||
      prevItem?.stop !== nextItem?.stop
    ) {
      lastFilteredRef.current = next;
      setFilteredItems(next);
    }
  }, [currentTime, myDocument?.document]);

  // Re-attach peaks whenever wave_info_url changes (e.g. after an owner
  // regenerates the audio timeline). onReady alone only fires once per media
  // mount.
  useEffect(() => {
    handleMediaElementReady(session?.wave_info_url);
  }, [handleMediaElementReady, session?.wave_info_url]);

  return (
    <div
      className={cn(
        'h-full flex flex-col overflow-y-auto rounded-2xl',
        cls.pageBg,
        cls.text
      )}
      style={vars}
    >
      <div
        ref={containerRef}
        className={cn(
          'relative w-full aspect-video flex-shrink-0',
          'border overflow-hidden rounded-sm',
          'max-h-[calc(100vh-370px)] lg:max-h-[calc(100vh-400px)]',
          css(styles.mainContainer, styles.mainText, styles.iconsColor),
          cls.border
        )}
      >
        <ReactPlayer
          ref={ref}
          url={session?.media_url ?? undefined}
          controls={false}
          onPlay={play}
          onPause={onMediaPause}
          playing={isPlaying}
          volume={1}
          muted={isMuted}
          onProgress={onProgress}
          playbackRate={playbackRate}
          autoPlay={false}
          progressInterval={300}
          onDuration={setDuration}
          width={'100%'}
          height={'100%'}
          fallback={
            <div className="flex justify-center items-center h-full">
              <Mic width={28} />
            </div>
          }
          onReady={() => handleMediaElementReady(session?.wave_info_url)}
        />

        {isAudio && (
          <div className="absolute inset-0 flex justify-center items-center h-full">
            <img src={audioThumbnail} alt="Audio Thumbnail" width={150} />
          </div>
        )}

        {/* Caption overlay - position is 0-100 percentage (0=top, 100=bottom) */}
        <AnimatePresence>
          {filteredItems?.map(item => {
            // Get position as percentage (0=top, 100=bottom, null/undefined=100)
            const pos = item.position;
            let positionPercent = 100; // default = bottom
            if (pos !== null && pos !== undefined) {
              const numPos = typeof pos === 'string' ? parseInt(pos, 10) : pos;
              if (!isNaN(numPos)) {
                positionPercent = Math.max(0, Math.min(100, numPos));
              }
            }
            // Map 0-100 to safe area (10%-90%) to keep captions visible within video bounds
            const safePosition = 10 + (positionPercent / 100) * 80;

            return (
              <div
                key={item.start}
                className="absolute left-0 right-0 py-2 px-4 flex justify-center"
                style={{
                  top: `${safePosition}%`,
                  transform: 'translateY(-50%)'
                }}
              >
                <motion.div
                  ref={divRef}
                  contentEditable
                  className="text-center flex items-center justify-center px-2 max-w-fit h-full focus:outline-0 overflow-auto rounded-2xl"
                  style={{
                    color: item.color || 'white',
                    backgroundColor: `rgba(0, 0, 0, 0.7)`,
                    fontWeight: item.bold ? 'bold' : 'normal',
                    textDecoration: `${item.underlined ? 'underline ' : ''}${
                      item.strike ? 'line-through' : ''
                    }`,
                    fontStyle: item.italic ? 'italic' : 'normal'
                  }}
                  dangerouslySetInnerHTML={{
                    __html: item.text.replace(/\n/g, '<br>')
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Bottom Controls Container */}
      <div className="flex flex-col flex-shrink-0">
        <div
          className={cn(
            'w-full px-1 py-2',
            'border-r border-l',
            cls.cardBg,
            cls.border,
            !hasMedia && 'opacity-50 pointer-events-none'
          )}
        >
          <SharedCaptionMediaSlider />
        </div>

        {/* Media Controls Bar */}
        <div
          className={cn(
            'border-x border-b border-t rounded-b-2xl overflow-x-auto flex-shrink-0 relative z-10 h-14 flex align-middle',
            cls.cardBg,
            cls.border,
            !hasMedia && 'opacity-50 pointer-events-none'
          )}
        >
          {/* Top highlight line */}
          <div
            className="absolute top-0 left-0 right-0 h-px"
            style={{
              background:
                'linear-gradient(90deg, transparent, rgba(255,255,255,0.2) 20%, rgba(255,255,255,0.2) 80%, transparent)'
            }}
          />

          <div className="flex items-center justify-between p-2 w-full">
            {/* Left section - Time Display & Offset */}
            <div className="flex items-center justify-center gap-2 flex-1">
              <div className="flex items-center gap-1.5">
                <span
                  className="text-[10px] uppercase tracking-wider font-medium hidden lg:inline"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('duration')}
                </span>
                <div
                  className="font-mono text-xs tabular-nums"
                  style={{ color: getColour('normalText') }}
                >
                  <span>{formatTime(currentTime)}</span>
                  <span className="mx-0.5 opacity-40">/</span>
                  <span className="opacity-60">
                    {formatTime(session?.duration || 0)}
                  </span>
                </div>
              </div>

              {/* Divider */}
              <div
                className="hidden sm:block w-px h-4 opacity-20"
                style={{ backgroundColor: getColour('normalText') }}
              />

              {/* Timing Offset Button */}
              <Tooltip label={t('timing-offset')}>
                <button
                  onClick={() => setIsTimingOffsetOpen(v => !v)}
                  className="flex items-center gap-1.5 pr-1 py-1 rounded-2xl transition-all hover:bg-black/5 dark:hover:bg-white/5 active:scale-95"
                  style={{
                    color: isTimingOffsetOpen
                      ? getColour('activeText')
                      : getColour('icon')
                  }}
                >
                  <Clock size={14} />
                </button>
              </Tooltip>
            </div>

            {/* Center section - Playback Controls */}
            <div
              className="flex items-center justify-center gap-1 px-2 border-x"
              style={{ borderColor: getColour('border') }}
            >
              <Tooltip
                label={shortcutLabel(t('rewind-5-seconds'), 'Ctrl+← / F7')}
              >
                <button
                  onClick={() => rewind()}
                  className={cn(
                    'h-7 w-7 rounded-full flex items-center justify-center transition-all',
                    'hover:bg-black/5 dark:hover:bg-white/5 active:scale-95'
                  )}
                  style={{ color: getColour('icon') }}
                >
                  <Undo2 size={14} />
                </button>
              </Tooltip>

              <Tooltip
                label={shortcutLabel(
                  isPlaying ? t('pause') : t('play'),
                  'Space / F5'
                )}
              >
                <button
                  onClick={() => (isPlaying ? pause() : play())}
                  className={cn(
                    'h-8 w-8 rounded-full flex items-center justify-center mx-0.5 transition-all',
                    'active:scale-95 shadow-sm text-white'
                  )}
                  style={{ backgroundColor: getColour('activeText') }}
                >
                  {isPlaying ? (
                    <Pause size={14} />
                  ) : (
                    <Play size={14} className="ml-0.5" />
                  )}
                </button>
              </Tooltip>

              <Tooltip
                label={shortcutLabel(t('forward-5-seconds'), 'Ctrl+→ / F8')}
              >
                <button
                  onClick={() => forward(5)}
                  className={cn(
                    'h-7 w-7 rounded-full flex items-center justify-center transition-all',
                    'hover:bg-black/5 dark:hover:bg-white/5 active:scale-95'
                  )}
                  style={{ color: getColour('icon') }}
                >
                  <Redo2 size={14} />
                </button>
              </Tooltip>
            </div>

            {/* Right section - Speed, Linked Playback & Style */}
            <div className="flex items-center justify-evenly gap-2 flex-1 px-2">
              {/* Speed Control */}
              <div className="flex items-center gap-1.5">
                <span
                  className="text-[10px] uppercase tracking-wider font-medium hidden lg:inline"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('speed')}
                </span>
                <select
                  value={playbackRate}
                  onChange={e => setPlaybackRate(Number(e.target.value))}
                  className={cn(
                    'h-6 px-1 rounded-2xl text-xs font-mono tabular-nums cursor-pointer',
                    'border bg-transparent focus:outline-none'
                  )}
                  style={{
                    color: getColour('normalText'),
                    borderColor: getColour('border')
                  }}
                >
                  {playbackRates.map(r => (
                    <option key={r.value} value={r.value}>
                      {r.label}x
                    </option>
                  ))}
                </select>
              </div>

              {/* Divider */}
              <div
                className="hidden sm:block w-px h-4 opacity-20"
                style={{ backgroundColor: getColour('normalText') }}
              />

              {/* Linked Playback */}
              <Tooltip
                label={shortcutLabel(
                  linkedPlayback
                    ? t('linked-playback-on')
                    : t('linked-playback-off'),
                  'Ctrl+L'
                )}
              >
                <button
                  onClick={toggleLinkedPlayback}
                  className={cn(
                    'flex items-center gap-1.5 px-2 py-1 rounded-2xl transition-all',
                    'hover:bg-black/5 dark:hover:bg-white/5 active:scale-95'
                  )}
                  style={{
                    color: linkedPlayback
                      ? getColour('activeText')
                      : getColour('icon')
                  }}
                >
                  {linkedPlayback ? <Link2 size={14} /> : <Unlink2 size={14} />}
                  <span
                    className="text-[10px] uppercase tracking-wider font-medium hidden lg:inline"
                    style={{
                      color: linkedPlayback
                        ? getColour('activeText')
                        : getColour('inactiveText')
                    }}
                  >
                    {t('linked')}
                  </span>
                </button>
              </Tooltip>

              {/* Divider */}
              <div
                className="hidden sm:block w-px h-4 opacity-20"
                style={{ backgroundColor: getColour('normalText') }}
              />

              {/* Style Button */}
              <Tooltip label={t('caption-styling')}>
                <button
                  onClick={() => setIsStyleModalOpen(true)}
                  className={cn(
                    'flex items-center gap-1.5 px-2 py-1 rounded-2xl transition-all',
                    'hover:bg-black/5 dark:hover:bg-white/10 active:scale-95'
                  )}
                  style={{ color: getColour('icon') }}
                >
                  <Palette size={14} />
                  <span
                    className="text-[10px] uppercase tracking-wider font-medium hidden lg:inline"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('style')}
                  </span>
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      {/* Timing Shift Control Modal */}
      <SharedTimingShiftControl
        isOpen={isTimingOffsetOpen}
        onClose={() => setIsTimingOffsetOpen(false)}
      />

      {/* Caption Style Modal */}
      <SharedCaptionStyleModal
        isOpen={isStyleModalOpen}
        onClose={() => setIsStyleModalOpen(false)}
      />
    </div>
  );
};
